import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { aiSupportApi } from "src/_api/aiSupportApi";
import {
  getChatHeaders,
  getChatUrl,
  newClientMessageId,
} from "src/_modules/AiSupportChat/adapters/craTransport";
import {
  CHAT_PHASES,
  CHAT_SIGNALS,
  apiError,
  chatReducer,
  clearRestoreText as clearRestoreTextAction,
  clearSignal as clearSignalAction,
  dismissNotice as dismissNoticeAction,
  feedbackSaved,
  getSendBlocker,
  hydrate,
  initialState,
  sendStarted,
  startDraft,
} from "src/_modules/AiSupportChat/core/chatReducer";
import { createChatTurn } from "src/_modules/AiSupportChat/core/chatTurn";
import { createFrameScheduler } from "src/_modules/AiSupportChat/core/deltaBatcher";
import { newTempId } from "src/_modules/AiSupportChat/core/ids";
import { findQuestion } from "src/_modules/AiSupportChat/core/messageText";
import { ERROR_CODES } from "src/_modules/AiSupportChat/core/protocol";
import { streamChat } from "src/_modules/AiSupportChat/core/streamChat";

/** `opening.status`: loading a saved conversation into the chat state. */
export const OPENING_STATUS = Object.freeze({
  IDLE: "idle",
  LOADING: "loading",
  FAILED: "failed",
});

const NOT_OPENING = Object.freeze({
  id: null,
  status: OPENING_STATUS.IDLE,
  error: null,
});

/** A 2xx body that isn't `{ conversation, messages }` for the asked id. */
const UNEXPECTED_BODY = Object.freeze({
  code: ERROR_CODES.PARSE,
  httpStatus: null,
  message: "",
});

const invalidate = (tags) => aiSupportApi.util.invalidateTags(tags);

/** A send whose turn hasn't ended yet (its response may still be closing). */
const isLive = (activeRef) =>
  activeRef.current !== null && !activeRef.current.turn.isEnded();

/**
 * The conversation state of the chat (plan §6.3), held by `AiChatProvider`
 * so it outlives the panel: closing it or changing routes never stops an
 * answer. Opens saved conversations, starts drafts, sends and streams.
 *
 * The open conversation lives in `chatReducer`, never in the RTK cache:
 * `GET /conversations/:id` is fetched once per open and hydrated, and the
 * streamed answer goes straight into the reducer (§5.1). Each send is a
 * `core/chatTurn` (event → action mapping, delta batching per frame).
 *
 * @param {{ maxMessageChars?: number }} [options] from `/eligibility`
 * @returns {{
 *   state: import("src/_modules/AiSupportChat/core/chatReducer").ChatState,
 *   opening: { id: string | null, status: string, error: object | null },
 *   openConversation: (id: string) => void,
 *   ensureConversation: (id: string) => void,
 *   startNew: (prefill?: string) => boolean,
 *   send: (text: string) => boolean,
 *   stop: () => void,
 *   retry: (messageId: string) => boolean,
 *   markFeedbackSaved: (feedback: { messageId: string, rating: string }) => void,
 *   dismissNotice: () => void,
 *   clearSignal: () => void,
 *   clearRestoreText: () => void,
 * }}
 */
export default function useAiChat({ maxMessageChars } = {}) {
  const [state, reactDispatch] = useReducer(chatReducer, initialState);
  const appDispatch = useDispatch();
  const [opening, setOpening] = useState(NOT_OPENING);
  // The state after every action dispatched so far, rendered or not, so
  // callbacks read it without changing identity, and a handler can act on
  // its own earlier dispatch (a chip: startNew, then send to that draft)
  const stateRef = useRef(state);
  const maxCharsRef = useRef(maxMessageChars);
  // The one request whose result may still be hydrated: { id, request }
  const pendingRef = useRef(null);
  // The send in flight: { turn, controller }. A ref, not state, so a double
  // submit in the same tick is refused before any render (T11: one request)
  const activeRef = useRef(null);

  /**
   * Applies the action to `stateRef` at once, then queues it for React. The
   * reducer is pure and React applies the queue in the same order, so the
   * rendered state catches up with the ref (D67). Never sync the ref from
   * a render instead: a render can commit while later actions still wait.
   */
  const dispatch = useCallback((action) => {
    stateRef.current = chatReducer(stateRef.current, action);
    reactDispatch(action);
  }, []);

  useLayoutEffect(() => {
    maxCharsRef.current = maxMessageChars;
  });

  /** Drops the pending open, so a late answer can't replace a newer view. */
  const cancelOpening = useCallback(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    pending.request.abort();
    setOpening(NOT_OPENING);
  }, []);

  const load = useCallback(
    (id) => {
      cancelOpening();
      // Subscribed only until it settles: the cache entry is collected
      // afterwards instead of lingering (D42)
      const request = appDispatch(
        aiSupportApi.endpoints.getAiConversation.initiate(id, {
          forceRefetch: true,
        })
      );
      const pending = { id, request };
      pendingRef.current = pending;
      setOpening({ id, status: OPENING_STATUS.LOADING, error: null });

      request
        .unwrap()
        .then(
          (body) => {
            if (pendingRef.current !== pending) return;
            pendingRef.current = null;
            if (body?.conversation?.id !== id) {
              setOpening({
                id,
                status: OPENING_STATUS.FAILED,
                error: UNEXPECTED_BODY,
              });
              return;
            }
            dispatch(hydrate(body));
            setOpening(NOT_OPENING);
          },
          (error) => {
            if (pendingRef.current !== pending) return;
            pendingRef.current = null;
            if (error?.code === ERROR_CODES.CONVERSATION_NOT_FOUND) {
              // Expired or deleted: signal "go home" + notFound notice
              setOpening(NOT_OPENING);
              dispatch(apiError(error, Date.now()));
              return;
            }
            setOpening({ id, status: OPENING_STATUS.FAILED, error });
          }
        )
        .finally(() => request.unsubscribe());
    },
    [appDispatch, cancelOpening, dispatch]
  );

  /**
   * Fetches a saved conversation and hydrates it, even if it is already
   * shown (fresh status / feedback). Nothing happens while an answer is in
   * flight: the live conversation must not be re-hydrated (C17), and
   * opening another one would orphan the stream (Q6; the UI disables it).
   */
  const openConversation = useCallback(
    (id) => {
      if (stateRef.current.phase !== CHAT_PHASES.IDLE) return;
      if (isLive(activeRef)) return;
      if (pendingRef.current?.id === id) return;
      load(id);
    },
    [load]
  );

  /**
   * Opens `id` unless it is already the loaded (or loading) conversation.
   * Not while the chat is heading home (855 dropped it): the view's effect
   * runs before the provider switches to home, and re-loading would hydrate
   * over the notFound notice and the unsent text.
   */
  const ensureConversation = useCallback(
    (id) => {
      const { conversation, signal } = stateRef.current;
      if (signal === CHAT_SIGNALS.GO_HOME) return;
      if (conversation && !conversation.isDraft && conversation.id === id) {
        return;
      }
      openConversation(id);
    },
    [openConversation]
  );

  /**
   * Replaces the conversation with an empty draft (no request until the
   * first message). Without `prefill` the draft takes the text a 855/856
   * couldn't send. Refused while an answer is in flight (C17).
   *
   * @param {string} [prefill]
   * @returns {boolean} whether a draft was started
   */
  const startNew = useCallback(
    (prefill) => {
      if (stateRef.current.phase !== CHAT_PHASES.IDLE) return false;
      if (isLive(activeRef)) return false;
      cancelOpening();
      dispatch(startDraft(typeof prefill === "string" ? prefill : undefined));
      return true;
    },
    [cancelOpening, dispatch]
  );

  /**
   * Sends `text` to the open conversation (or creates one from the draft)
   * and streams the answer. Refused — nothing sent — while a turn is live,
   * when the text is blank or longer than `maxMessageChars`, or when
   * `getSendBlocker` says so (closed, cooldown, unavailable).
   *
   * @param {string} value
   * @returns {boolean} whether it was sent (the composer clears on true)
   */
  const send = useCallback(
    (value) => {
      const text = typeof value === "string" ? value.trim() : "";
      if (!text || isLive(activeRef)) return false;
      const current = stateRef.current;
      if (getSendBlocker(current, Date.now()) !== null) return false;
      const max = maxCharsRef.current;
      if (Number.isFinite(max) && max > 0 && text.length > max) return false;

      // A late open would replace the conversation under the live turn
      cancelOpening();
      const { conversation } = current;
      const conversationId =
        conversation && !conversation.isDraft ? conversation.id : null;
      dispatch(
        sendStarted({
          tempUserId: newTempId("tmp-user"),
          tempAssistantId: newTempId("tmp-assistant"),
          text,
          now: Date.now(),
        })
      );

      const controller = new AbortController();
      const turn = createChatTurn({
        dispatch,
        ...createFrameScheduler(),
        conversationId,
        // The new conversation (and the one it closed) shows on home while
        // the answer streams, so Back → its row → back here
        onMeta: ({ isNew }) => {
          if (isNew) appDispatch(invalidate(["AiConversations"]));
        },
        onDone: ({ conversationId: id }) => {
          appDispatch(
            invalidate(
              id
                ? ["AiConversations", { type: "AiConversation", id }]
                : ["AiConversations"]
            )
          );
        },
        onAbandon: () => controller.abort(),
      });
      const entry = { turn, controller };
      activeRef.current = entry;

      // Omit `conversationId` for a draft: the backend rejects null (400)
      const body = { clientMessageId: newClientMessageId(), message: text };
      if (conversationId) body.conversationId = conversationId;
      streamChat({
        url: getChatUrl(),
        headers: getChatHeaders(),
        body,
        signal: controller.signal,
        onEvent: turn.handleEvent,
      })
        .then(turn.finish, (error) => {
          turn.fail(error, Date.now());
          // 856: it went idle meanwhile, so its row on home is stale
          // (opening always refetches the detail)
          if (error?.code === ERROR_CODES.CONVERSATION_CLOSED) {
            appDispatch(invalidate(["AiConversations"]));
          }
        })
        .finally(() => {
          if (activeRef.current === entry) activeRef.current = null;
        });
      return true;
    },
    [appDispatch, cancelOpening, dispatch]
  );

  /** Stop: the answer keeps its text as `aborted` (the server saves it too). */
  const stop = useCallback(() => {
    if (!isLive(activeRef)) return;
    const { controller, turn } = activeRef.current;
    controller.abort();
    turn.stop();
  }, []);

  /**
   * Re-sends the question of a failed / interrupted answer as a new message
   * (a new `clientMessageId`; the failed pair stays in the history).
   *
   * @param {string} messageId the answer's id (a temp id before `meta`)
   * @returns {boolean} whether it was sent
   */
  const retry = useCallback(
    (messageId) => send(findQuestion(stateRef.current.messages, messageId)),
    [send]
  );

  /**
   * `POST /messages/:id/feedback` succeeded (FeedbackButtons sends it): the
   * open conversation shows the rating. No cache to update, opening a
   * conversation always refetches it (D29).
   */
  const markFeedbackSaved = useCallback(
    ({ messageId, rating }) => dispatch(feedbackSaved({ messageId, rating })),
    [dispatch]
  );

  const dismissNotice = useCallback(
    () => dispatch(dismissNoticeAction()),
    [dispatch]
  );
  const clearSignal = useCallback(
    () => dispatch(clearSignalAction()),
    [dispatch]
  );
  const clearRestoreText = useCallback(
    () => dispatch(clearRestoreTextAction()),
    [dispatch]
  );

  // Logout / user switch unmounts the provider: drop the pending open and
  // abort the stream (the server saves the partial answer as `aborted`)
  useEffect(() => cancelOpening, [cancelOpening]);
  useEffect(
    () => () => {
      const active = activeRef.current;
      activeRef.current = null;
      if (!active) return;
      active.turn.dispose();
      active.controller.abort();
    },
    []
  );

  return useMemo(
    () => ({
      state,
      opening,
      openConversation,
      ensureConversation,
      startNew,
      send,
      stop,
      retry,
      markFeedbackSaved,
      dismissNotice,
      clearSignal,
      clearRestoreText,
    }),
    [
      state,
      opening,
      openConversation,
      ensureConversation,
      startNew,
      send,
      stop,
      retry,
      markFeedbackSaved,
      dismissNotice,
      clearSignal,
      clearRestoreText,
    ]
  );
}
